/* eslint-disable camelcase */
/* Drupal data is snake_case */
/* eslint-disable react/no-danger */
import React, {
  useEffect, useState, useRef, useMemo, useCallback, useContext,
} from 'react';

import {
  useMapStateStore, useMapDataStore, useConfigStore, useAppStore,
  useMapInteractionStore, useWindowStore,
} from '../../../stores';
import { findPropertyById } from '../../../utils/common';
import {
  parseViewBox, getClusterViewBox, normalizeZoomZones,
  getFallbackLevelSvgId, buildStatusUpdates, categorizeUnits, findLevelSvgIdForSvgId,
} from './MapOverlayHelpers';
import { generatePlaceShapesMarkup } from '../MapPlace/MapPlaceHelpers';
import { generateUnitShapesMarkup } from '../MapUnit/MapUnitHelpers';
import SiteSeeContext from '../../SiteSeeGuides/SiteSeeContext';
import { getPulseTarget, PULSE_TARGETS } from '../../SiteSeeGuides/SiteSeeGuideData';
import MapChunk from '../MapChunk';
// eslint-disable-next-line import/no-cycle
import MapLevel from '../MapLevel/MapLevel';
import MapOverlayBaseRenderer from './MapOverlayBaseRenderer';
import MapOverlayLabels from './MapOverlayLabels';
import MapOverlayModals from './MapOverlayModals';

import './MapOverlay.scss';

const MapOverlay = () => {
  // ── Refs ──────────────────────────────────────────────────────────────
  const isMountedRef = useRef(true);
  const containerRef = useRef(null);
  const statusUpdatesRef = useRef({});

  // ── Context ───────────────────────────────────────────────────────────
  const siteSeeContext = useContext(SiteSeeContext);
  const { siteSeeGuideId } = siteSeeContext || {};

  // ── Store selectors: config ───────────────────────────────────────────
  const mapConfig = useConfigStore((state) => state.mapConfig);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const config = useConfigStore((state) => state.config);
  const settings = useAppStore((state) => state.settings);
  // Draft styling is a preview-only affordance; see MapPlaceHelpers (#552).
  const previewGranted = useAppStore((state) => state.previewGranted);

  // ── Store selectors: map state ────────────────────────────────────────
  const activeLevelSvgId = useMapStateStore((state) => state.activeLevelSvgId);
  const initialLevelSvgId = useMapStateStore((state) => state.initialLevelSvgId);
  const setActiveLevelSvgId = useMapStateStore((state) => state.setActiveLevelSvgId);
  const svgFocusId = useMapStateStore((state) => state.svgFocusId);
  const pendingAmenityModal = useMapStateStore((state) => state.pendingAmenityModal);
  const setPendingAmenityModal = useMapStateStore((state) => state.setPendingAmenityModal);
  const focusAnimationComplete = useMapStateStore((state) => state.focusAnimationComplete);
  const setFocusAnimationComplete = useMapStateStore((state) => state.setFocusAnimationComplete);
  const setAmenityModalOpen = useMapStateStore((state) => state.setAmenityModalOpen);
  const activeZoom = useMapStateStore((state) => state.activeZoom);
  const effectiveMaxZoom = useMapStateStore((state) => state.effectiveMaxZoom);
  const activeMapType = useMapStateStore((state) => state.activeMapType);
  const POIVisible = useMapStateStore((state) => state.POIVisible);

  // ── Store selectors: map data ─────────────────────────────────────────
  const svgData = useMapDataStore((state) => state.svgData);
  const residenceStatusOptions = useMapDataStore((state) => state.residenceStatusOptions);

  // ── Store selectors: map interaction ──────────────────────────────────
  const activeLevelOfCareUuid = useMapInteractionStore((state) => state.activeLevelOfCareUuid);
  const mapActiveUnitData = useMapInteractionStore((state) => state.mapActiveUnitData);
  const changedUnits = useMapInteractionStore((state) => state.changedUnits);
  const setActiveLevelOfCareUuid = useMapInteractionStore(
    (state) => state.setActiveLevelOfCareUuid,
  );
  const markersVisible = useMapInteractionStore((state) => state.markersVisible);
  const setFocusXYZ = useMapInteractionStore((state) => state.setFocusXYZ);

  // ── Store selectors: window ───────────────────────────────────────────
  const isMobile = useWindowStore((state) => state.isMobile);

  // ── Local state: SVG / level data ─────────────────────────────────────
  const [activeLevelSvgData, setActiveLevelSvgData] = useState(null);
  const [unitsSvgData, setUnitsSvgData] = useState({});
  const [placesSvgData, setPlacesSvgData] = useState({});
  const [levelOtherSvgData, setLevelOtherSvgData] = useState(null);

  // ── Local state: categorized units ────────────────────────────────────
  // categorizeUnits also returns activeUnits / disabledUnits / inactiveUnits.
  // Nothing renders those splits — MapLabels and MapUnit read display mode off
  // each unit in `allUnits` — so they are deliberately not stored.
  const [units, setUnits] = useState([]);
  const [unitsVersion, setUnitsVersion] = useState(0);

  // ── Local state: places ───────────────────────────────────────────────
  const [places, setPlaces] = useState([]);

  // ── Local state: level fade transition ───────────────────────────────
  const [levelFading, setLevelFading] = useState(false);
  const pendingLevelRef = useRef(null);

  // ── Local state: container & modal ────────────────────────────────────
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [modalState, setModalState] = useState({ isOpen: false, data: null, type: null });
  const [modalNavBtns, setModalNavBtns] = useState(null);
  const [modalHeaderData, setModalHeaderData] = useState(null);

  // ── Derived values: config destructuring ──────────────────────────────
  const { platform } = settings || {};
  const { defaultLevelOfCare } = config || {};
  const { birdsEyeLevelId, baseColors, googleMapsConfig } = mapConfig || {};

  const { zooms } = googleMapsConfig || {};
  const {
    max: maxZoom,
    enableLevels: mapZoomToEnableLevels,
    enableZoomZones: zoomZonesActivationLevel,
  } = zooms || {};

  const {
    platformClusterPlaceLabels,
    platformShowOccupancy,
    platformShowMapPlaceLabelsInitially,
    platformShowLowerValuePlaceLabelsInitially,
    platformShowZoomZonesOnMapLevels,
  } = featureFlags || {};

  const {
    unitStroke = 'black',
    unitFill = 'white',
    placeFill = 'white',
    placeStroke = 'black',
  } = baseColors || {};

  // ── Derived values: SVG attributes ────────────────────────────────────
  const { attributes_array: attrArray } = svgData || {};
  const { viewBox: vbString, parsed_view_box: parsedVb } = attrArray || {};

  const {
    attributes_array,
    tagless_defs_markup,
    tagless_style_markup,
    bg_markup,
  } = svgData || {};

  const {
    version, width, height, viewBox, parsed_view_box,
  } = attributes_array || {};

  const { birds_eye_view } = svgData || {};
  const { markup: birdsEyeMarkup, zoom_zones: rawZoomZones } = birds_eye_view || {};

  const {
    units: unitsSvgDataRaw,
    places: placesSvgDataRaw,
    other: levelOtherSvgDataRaw,
  } = activeLevelSvgData || {};

  // ── Derived values: computed flags ────────────────────────────────────
  const svgLevels = svgData?.levels;
  const isBirdsEye = activeLevelSvgId === birdsEyeLevelId || !activeLevelSvgId;
  const zoomAllowsLevels = activeZoom >= mapZoomToEnableLevels;
  const mapControlsAllowLevels = activeMapType === 'roadmap' && !POIVisible;
  const modalRootId = platform === 'page' ? 'pibPage-mapModalRoot' : 'modalRoot';

  const pulseTarget = getPulseTarget(siteSeeGuideId, isMobile);
  const isZoomZonesGuideActive = pulseTarget === PULSE_TARGETS.ZOOM_ZONES;
  const isAmenitiesGuideActive = pulseTarget === PULSE_TARGETS.AMENITIES;
  const isResidencesGuideActive = pulseTarget === PULSE_TARGETS.RESIDENCES;

  // ── useMemo ───────────────────────────────────────────────────────────
  const clusterViewBox = useMemo(
    () => getClusterViewBox(parsedVb, vbString),
    [parsedVb, vbString],
  );

  const fallbackLevelSvgId = useMemo(
    () => getFallbackLevelSvgId(svgLevels, initialLevelSvgId, birdsEyeLevelId),
    [svgLevels, initialLevelSvgId, birdsEyeLevelId],
  );

  const zoomZones = useMemo(
    () => normalizeZoomZones(rawZoomZones),
    [rawZoomZones],
  );

  // Count of unique visible places per zone grouping, across every level and
  // every level of care. Dedupes by place uuid so places that fan out to
  // multiple levels are counted once. Includes lower-value (non-link) places
  // only when the platform is configured to show their labels.
  const linkedPlaceCountsByZone = useMemo(() => {
    const seenByZone = new Map();
    Object.values(svgData?.levels || {}).forEach((level) => {
      Object.values(level?.places || {}).forEach((p) => {
        const groupingUuid = p?.map_building_grouping?.uuid;
        if (!groupingUuid) return;
        const pin = p?.pin;
        const isLink = pin?.is_link !== false
          && (pin?.link_url || pin?.modal_nid || pin?.create_modal);
        if (!isLink && !platformShowLowerValuePlaceLabelsInitially) return;
        const placeKey = pin?.uuid || pin?.svg_id;
        if (!placeKey) return;
        if (!seenByZone.has(groupingUuid)) seenByZone.set(groupingUuid, new Set());
        seenByZone.get(groupingUuid).add(placeKey);
      });
    });
    const counts = new Map();
    seenByZone.forEach((set, zoneUuid) => counts.set(zoneUuid, set.size));
    return counts;
  }, [svgData, platformShowLowerValuePlaceLabelsInitially]);

  const birdsEyeZoomZones = useMemo(() => {
    if (!zoomZones) return {};
    const result = {};
    Object.entries(zoomZones).forEach(([key, zone]) => {
      result[key] = {
        ...zone,
        revealed_count: zone?.uuid ? (linkedPlaceCountsByZone.get(zone.uuid) || 0) : 0,
      };
    });
    return result;
  }, [zoomZones, linkedPlaceCountsByZone]);

  const parsedViewBox = useMemo(
    () => parseViewBox(parsed_view_box, viewBox),
    [parsed_view_box, viewBox],
  );

  const unitShapesMarkup = useMemo(() => {
    if (isBirdsEye || !units || units.length === 0) return '';
    return generateUnitShapesMarkup(units, unitFill, unitStroke, platformShowOccupancy);
    // Deliberately partial dep list — see the note above. (The react-hooks plugin
    // is not enabled in .eslintrc, so a disable directive naming it is itself a
    // lint finding; this comment records the intent instead.)
  }, [isBirdsEye, units, unitFill, unitStroke, platformShowOccupancy, unitsVersion]);

  const placeShapesMarkup = useMemo(() => {
    if (isBirdsEye || !places || places.length === 0) return '';
    return generatePlaceShapesMarkup(
      places,
      placeFill,
      placeStroke,
      unitFill,
      unitStroke,
      previewGranted === true,
    );
  }, [isBirdsEye, places, placeFill, placeStroke, unitFill, unitStroke, previewGranted]);

  const levelMarkup = useMemo(() => {
    if (isBirdsEye) {
      return <MapChunk markupString={birdsEyeMarkup} />;
    }
    return <MapLevel levelOtherSvgData={levelOtherSvgData} />;
  }, [isBirdsEye, birdsEyeMarkup, levelOtherSvgData]);

  // ── useCallback ───────────────────────────────────────────────────────
  const handleMarkerClick = useCallback((place) => {
    if (place?.pin?.create_modal) {
      setModalState({ isOpen: true, data: place, type: 'place' });
      setAmenityModalOpen(true);
    }
  }, [setAmenityModalOpen]);

  const handleUnitClick = useCallback((event) => {
    let { target } = event;
    while (target && !target.dataset?.svgId && target.parentElement) {
      target = target.parentElement;
    }

    const svgId = target?.dataset?.svgId;
    if (!svgId) return;

    const unit = units.find((u) => u.attributes?.id === svgId);
    if (!unit) return;

    if (unit.displayMode !== 'active') return;

    setModalState({ isOpen: true, data: unit, type: 'unit' });
  }, [units]);

  const handleModalClose = useCallback(() => {
    setModalState({ isOpen: false, data: null, type: null });
    setModalNavBtns(null);
    setModalHeaderData(null);
    setAmenityModalOpen(false);
  }, [setAmenityModalOpen]);

  // ── useEffect: cleanup ────────────────────────────────────────────────
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  // ── useEffect: container size tracking ────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateSize = () => {
      requestAnimationFrame(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => observer.disconnect();
  }, [activeLevelSvgId]);

  // ── useEffect: focus svg id data ─────────────────────────────
  useEffect(() => {
    if (svgFocusId && svgData) {
      const data = findPropertyById(svgData, svgFocusId);
      if (!data || !data.center) {
        console.warn('[MapOverlay] No SVG data found for svgFocusId:', svgFocusId);
        return;
      }
      const levelId = findLevelSvgIdForSvgId(svgFocusId, svgLevels);
      // Preserve current zoom if it's already past the levels threshold so
      // back-to-back amenity selections that each need cluster-reveal zoom
      // don't bounce out to mapZoomToEnableLevels and then back in.
      const currentZoom = useMapStateStore.getState().activeZoom;
      const targetZ = currentZoom && currentZoom > mapZoomToEnableLevels
        ? currentZoom
        : mapZoomToEnableLevels;
      const XYZ = { x: data.center.x, y: data.center.y, z: targetZ };
      // Auto-focus the LoC filter when the focused SVG resolves to a single
      // care level. Units carry a scalar LoC; places carry an array (multi-
      // value). Multi-LoC places skip auto-focus — no single answer.
      const focusLoc = data?.level_of_care;
      if (Array.isArray(focusLoc)) {
        if (focusLoc.length === 1) {
          setActiveLevelOfCareUuid(focusLoc[0].uuid);
        }
      } else if (focusLoc?.uuid) {
        setActiveLevelOfCareUuid(focusLoc.uuid);
      }
      if (levelId) {
        setActiveLevelSvgId(levelId);
      }
      setFocusAnimationComplete(false);
      setFocusXYZ(XYZ);
    } else {
      setFocusXYZ(null);
    }
  }, [svgFocusId, svgData, mapZoomToEnableLevels]);

  // ── useEffect: open amenity modal after map animation completes ────────
  // Wait for animation to finish, then find the matching place once level
  // data (places) has loaded. Don't clear the flags on a failed match —
  // places may still be loading from a level transition.
  useEffect(() => {
    if (!pendingAmenityModal || !focusAnimationComplete || !svgFocusId || places.length === 0) {
      return undefined;
    }

    const matchingPlace = places.find((p) => p?.pin?.svg_id === svgFocusId);
    if (!matchingPlace?.pin?.create_modal) return undefined;

    const POST_ANIMATION_DELAY = 4000;
    const timer = setTimeout(() => {
      setModalState({ isOpen: true, data: matchingPlace, type: 'place' });
      setAmenityModalOpen(true);
      setPendingAmenityModal(false);
    }, POST_ANIMATION_DELAY);

    return () => clearTimeout(timer);
  }, [pendingAmenityModal, focusAnimationComplete, svgFocusId, places]);

  // ── useEffect: toggle level details based on zoom while focused ────────
  // Revert to birds-eye when zoomed below threshold; restore focused level
  // when zooming back in so the highlighted amenity/residence reappears.
  useEffect(() => {
    if (!svgFocusId || !mapZoomToEnableLevels || activeZoom == null) return;
    if (activeZoom < mapZoomToEnableLevels) {
      setActiveLevelSvgId(birdsEyeLevelId || fallbackLevelSvgId);
    } else {
      const levelId = findLevelSvgIdForSvgId(svgFocusId, svgLevels);
      if (levelId) {
        setActiveLevelSvgId(levelId);
      }
    }
  }, [activeZoom, svgFocusId, mapZoomToEnableLevels, svgData,
    fallbackLevelSvgId, birdsEyeLevelId]);

  // ── useEffect: initial level of care ──────────────────────────────────
  useEffect(() => {
    if (!svgFocusId) {
      setActiveLevelOfCareUuid(defaultLevelOfCare);
    }
  }, [defaultLevelOfCare, svgFocusId, setActiveLevelOfCareUuid]);

  // ── useEffect: active level SVG data (with fade transition) ──────────
  useEffect(() => {
    if (!isMountedRef.current) return undefined;
    if (!svgData?.levels || !activeLevelSvgId) return undefined;

    const newData = svgData.levels[activeLevelSvgId];

    // First render — no existing data to fade from
    if (!activeLevelSvgData) {
      setActiveLevelSvgData(newData);
      return undefined;
    }

    // Same data — skip
    if (newData === activeLevelSvgData) return undefined;

    // Fade out, swap, fade in
    pendingLevelRef.current = newData;
    setLevelFading(true);

    const FADE_DURATION = 200;
    const timer = setTimeout(() => {
      if (!isMountedRef.current) return;
      setActiveLevelSvgData(pendingLevelRef.current);
      pendingLevelRef.current = null;
      setLevelFading(false);
    }, FADE_DURATION);

    return () => clearTimeout(timer);
  }, [svgData, activeLevelSvgId]);

  // ── useEffect: determine active level based on focus / zoom ───────────
  useEffect(() => {
    // When svgFocusId is active, the focus effect owns the level — don't let
    // the zoom-based logic overwrite it. Without this, focusing into a level
    // before the zoom animation has caught up would get clobbered back to
    // birds-eye, and the focused level's data would never load.
    if (svgFocusId) return;

    if (zoomAllowsLevels && mapControlsAllowLevels) {
      const currentLevel = useMapStateStore.getState().activeLevelSvgId;
      if (currentLevel && svgLevels?.[currentLevel]) {
        setActiveLevelSvgId(currentLevel);
      } else if (initialLevelSvgId && svgLevels?.[initialLevelSvgId]) {
        setActiveLevelSvgId(initialLevelSvgId);
      } else if (birdsEyeLevelId || fallbackLevelSvgId) {
        setActiveLevelSvgId(birdsEyeLevelId || fallbackLevelSvgId);
      }
    } else if (birdsEyeLevelId || fallbackLevelSvgId) {
      setActiveLevelSvgId(birdsEyeLevelId || fallbackLevelSvgId);
    }
  }, [svgFocusId, svgLevels, initialLevelSvgId, birdsEyeLevelId, zoomAllowsLevels,
    mapControlsAllowLevels, setActiveLevelSvgId, fallbackLevelSvgId]);

  // ── useEffect: propagate level SVG data to local state ────────────────
  useEffect(() => {
    if (!isMountedRef.current) return;
    setUnitsSvgData(unitsSvgDataRaw || {});
    setPlacesSvgData(placesSvgDataRaw);
    setLevelOtherSvgData(levelOtherSvgDataRaw);
  }, [unitsSvgDataRaw, placesSvgDataRaw, levelOtherSvgDataRaw]);

  // ── useEffect: process status updates from changedUnits ───────────────
  useEffect(() => {
    if (changedUnits?.length > 0) {
      const updates = buildStatusUpdates(changedUnits, residenceStatusOptions);
      Object.assign(statusUpdatesRef.current, updates);
      setUnitsVersion((v) => v + 1);
    }
  }, [changedUnits, residenceStatusOptions]);

  // ── useEffect: categorize units ───────────────────────────────────────
  useEffect(() => {
    if (
      unitsSvgData
      && Object.keys(unitsSvgData).length > 0
      && mapActiveUnitData
    ) {
      const focusIsUnit = svgFocusId && unitsSvgData[svgFocusId];
      const result = categorizeUnits(
        unitsSvgData,
        mapActiveUnitData,
        focusIsUnit ? svgFocusId : null,
        activeLevelOfCareUuid,
        statusUpdatesRef.current,
      );
      setUnits(result.allUnits);
    } else {
      setUnits([]);
    }
    // Deliberately partial dep list — see the note above. (The react-hooks plugin
    // is not enabled in .eslintrc, so a disable directive naming it is itself a
    // lint finding; this comment records the intent instead.)
  }, [svgFocusId, activeLevelOfCareUuid, unitsSvgData, mapActiveUnitData, unitsVersion]);

  // ── useEffect: places ─────────────────────────────────────────────────
  // Places without a level_of_care stay active across all LOCs (amenities
  // like pools, lobbies). Places tagged with a LOC are still rendered when
  // they don't match the active selection, but tagged as 'disabled' so the
  // shape paints in neutral unit colors and no label/marker is shown.
  useEffect(() => {
    if (!placesSvgData) {
      setPlaces([]);
      return;
    }
    const allPlaces = Object.values(placesSvgData);
    const categorized = allPlaces.map((p) => {
      if (!activeLevelOfCareUuid) return { ...p, displayMode: 'active' };
      const locs = p?.level_of_care;
      if (!locs?.length) return { ...p, displayMode: 'active' };
      const matches = locs.some((loc) => loc.uuid === activeLevelOfCareUuid);
      return { ...p, displayMode: matches ? 'active' : 'disabled' };
    });
    setPlaces(categorized);
  }, [placesSvgData, activeLevelOfCareUuid]);

  const activePlaces = useMemo(
    () => places.filter((p) => p.displayMode !== 'disabled'),
    [places],
  );

  // Per-zone augmented zoomZones to render as navigation labels on a floor.
  // Every zone is included (except the most-recently-clicked one). A zone
  // renders as a place-style dot (`has_active_content: true`) when any of:
  //   - it has at least one residence or place on this floor matching the
  //     active LoC, AND either has no LoC tag of its own (LoC-agnostic) or
  //     its own LoC list includes the active LoC; OR
  //   - the zone has no LoC of its own AND every place at this location on
  //     this floor has no LoC (a generic amenities-only location).
  // Otherwise it uses the full zoom icon.
  const floorZoomZones = useMemo(() => {
    if (!zoomZones || isBirdsEye) return {};

    const matchingPlaceLocUuids = new Set();
    const placeCountsByZone = new Map();
    places.forEach((p) => {
      const groupingUuid = p?.map_building_grouping?.uuid;
      if (!groupingUuid) return;
      const locs = p?.level_of_care;
      const stats = placeCountsByZone.get(groupingUuid) || { total: 0, noLoc: 0 };
      stats.total += 1;
      if (!locs?.length) stats.noLoc += 1;
      placeCountsByZone.set(groupingUuid, stats);
      const matchesActiveLoc = !activeLevelOfCareUuid
        || (locs?.length && locs.some((loc) => loc?.uuid === activeLevelOfCareUuid));
      if (matchesActiveLoc) matchingPlaceLocUuids.add(groupingUuid);
    });
    const noLocOnlyPlaceZones = new Set();
    placeCountsByZone.forEach((stats, zoneId) => {
      if (stats.total > 0 && stats.total === stats.noLoc) {
        noLocOnlyPlaceZones.add(zoneId);
      }
    });

    const matchingUnitLocUuids = new Set();
    units.forEach((u) => {
      const groupingUuid = u?.info?.map_building_grouping?.uuid;
      if (!groupingUuid) return;
      const unitLocUuid = u?.info?.level_of_care?.uuid;
      const matchesActiveLoc = !activeLevelOfCareUuid
        || unitLocUuid === activeLevelOfCareUuid;
      if (matchesActiveLoc) matchingUnitLocUuids.add(groupingUuid);
    });

    const result = {};
    Object.entries(zoomZones).forEach(([key, zone]) => {
      const zoneUuid = zone?.uuid;
      if (!zoneUuid) return;
      const zoneLocs = Array.isArray(zone?.level_of_care) ? zone.level_of_care : [];
      const zoneHasOwnLoc = zoneLocs.length > 0;
      // Zones without their own LoC tag are LoC-agnostic — they match every
      // active LoC, mirroring how no-LoC places are treated as matching every
      // LoC above. Without this, a multi-use zone (no zone LoC) with on-floor
      // units that match the active LoC would fail rule #1 and arrow-style.
      const zoneIncludesActiveLoc = !activeLevelOfCareUuid
        || !zoneHasOwnLoc
        || zoneLocs.some((loc) => loc?.uuid === activeLevelOfCareUuid);
      const hasMatchingContent = matchingPlaceLocUuids.has(zoneUuid)
        || matchingUnitLocUuids.has(zoneUuid);
      // No-LoC-only-places rule only applies to zones without their own LoC —
      // i.e. genuine amenity-only locations like a club house. Zones with a
      // designated LoC (e.g. an Independent Living building) shouldn't be
      // dot-styled just because their on-floor places happen to lack LoC tags.
      const hasActiveContent = (zoneIncludesActiveLoc && hasMatchingContent)
        || (!zoneHasOwnLoc && noLocOnlyPlaceZones.has(zoneUuid));

      result[key] = {
        ...zone,
        has_active_content: hasActiveContent,
        revealed_count: linkedPlaceCountsByZone.get(zoneUuid) || 0,
      };
    });
    return result;
  }, [zoomZones, isBirdsEye, places, units, linkedPlaceCountsByZone,
    activeLevelOfCareUuid]);

  // ── Render ────────────────────────────────────────────────────────────
  return activeLevelSvgId ? (
    <div className="svgMapWrapper" ref={containerRef}>
      <MapOverlayBaseRenderer
        version={version}
        width={width}
        height={height}
        viewBox={viewBox}
        tagless_defs_markup={tagless_defs_markup}
        tagless_style_markup={tagless_style_markup}
        bg_markup={bg_markup}
        levelContent={levelMarkup}
        unitShapesMarkup={unitShapesMarkup}
        placeShapesMarkup={placeShapesMarkup}
        onUnitClick={handleUnitClick}
        units={units}
        platformShowOccupancy={platformShowOccupancy}
        unitFill={unitFill}
        unitStroke={unitStroke}
        isResidencesGuideActive={isResidencesGuideActive}
        levelFading={levelFading}
      />

      <MapOverlayLabels
        places={activePlaces}
        units={units}
        unitsVersion={unitsVersion}
        isBirdsEye={isBirdsEye}
        floorZoomZones={floorZoomZones}
        defaultLevelOfCare={defaultLevelOfCare}
        parsedViewBox={parsedViewBox}
        clusterViewBox={clusterViewBox}
        containerSize={containerSize}
        activeZoom={activeZoom}
        maxZoom={maxZoom}
        effectiveMaxZoom={effectiveMaxZoom}
        mapZoomToEnableLevels={mapZoomToEnableLevels}
        zoomZonesActivationLevel={zoomZonesActivationLevel}
        platformClusterPlaceLabels={platformClusterPlaceLabels}
        platformShowOccupancy={platformShowOccupancy}
        platformShowMapPlaceLabelsInitially={platformShowMapPlaceLabelsInitially}
        platformShowLowerValuePlaceLabelsInitially={platformShowLowerValuePlaceLabelsInitially}
        platformShowZoomZonesOnMapLevels={platformShowZoomZonesOnMapLevels}
        markersVisible={markersVisible}
        isAmenitiesGuideActive={isAmenitiesGuideActive}
        isZoomZonesGuideActive={isZoomZonesGuideActive}
        zoomZones={birdsEyeZoomZones}
        svgData={svgData}
        onMarkerClick={handleMarkerClick}
      />

      <MapOverlayModals
        modalState={modalState}
        modalRootId={modalRootId}
        modalNavBtns={modalNavBtns}
        modalHeaderData={modalHeaderData}
        onClose={handleModalClose}
        setModalNavBtns={setModalNavBtns}
        setModalHeaderData={setModalHeaderData}
      />
    </div>
  ) : null;
};

export default MapOverlay;
